import React, { Component } from "react"
import { Accordion, Card, FormControl, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap"
import Select from "react-select"
import moment from "moment"
import DatePicker from "src/components/common/DatePicker"
import snackBar from "src/utilities/snackBar/SnackBar"
import { shouldAllowAccess } from "src/utilities/helper"
import { getVault77ErrorMessage } from "src/components/vault77/core/apiError"
import { formatForDisplay } from "src/components/vault77/core/MaskedDataField"
import { getCurrentPayRate } from "src/components/vault77/payRateHistory/api/payRateHistory.api"
import type { PayRateHistory } from "src/components/vault77/payRateHistory/types"
import {
    getEmployeeSensitiveDataByEmployeeId,
    createEmployeeSensitiveData,
    updateEmployeeSensitiveData,
} from "src/components/vault77/employeeSensitiveData/api/employeeSensitiveData.api"
import type { EmployeeSensitiveData } from "src/components/vault77/employeeSensitiveData/types"
import { HealthRefreshContext, type HealthRefreshContextValue } from "src/components/vault77/health/HealthRefreshContext"
import PayRateModal from "src/components/vault77/payRateHistory/PayRateModal"
import "./UserSecure.css"

interface UserSecureProps {
    userId: number
    manageParentState: Function
    secureIsDirty: boolean
    readOnly?: boolean
    // Supervisor lives in this card (as it did on the Area51 secure form) but saves through
    // the regular employee payload: the picker only lifts the value into UserAdmin's employee
    // state (isDirty + the normal Save). All four props come from UserAdmin; when
    // `supervisors` is absent the field doesn't render.
    supervisorId?: number | null
    supervisorName?: string | null
    supervisors?: Array<{ id: number; text: string }>
    onSupervisorChange?: (id: number, name: string) => void
}

// Editable sensitive fields only (audit/id columns excluded). "" represents null.
interface SensitiveDraft {
    SSN: string
    HomePhone: string
    HomeEmail: string
    Birthdate: string
    Gender: string
    MaritalStatus: string
    Ethnicity: string
    VeteranStatus: string
}

type UserSecureState = {
    loading: boolean
    payRate: PayRateHistory | null
    record: EmployeeSensitiveData | null
    draft: SensitiveDraft
    pristine: SensitiveDraft
    showPayRateModal: boolean
}

// Progressive phone formatter: (XXX) XXX-XXXX, capped at 10 digits (fits nvarchar(20)).
function formatPhoneInput(value: string): string {
    const digits = value.replace(/\D/g, "").slice(0, 10)
    if (digits.length < 4) return digits
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const EMPTY_DRAFT: SensitiveDraft = {
    SSN: "",
    HomePhone: "",
    HomeEmail: "",
    Birthdate: "",
    Gender: "",
    MaritalStatus: "",
    Ethnicity: "",
    VeteranStatus: "",
}

// Placeholder shown in place of PII values for callers without Vault77.Read.EmployeeSensitiveData.
// The real values are never fetched for them (and the API would only return nulls anyway) — the
// stars are a static stand-in so the card keeps its full shape for every viewer.
// Fixed option lists copied VERBATIM from the Area51 secure form's dropdowns
// (LaunchPointArea51Web/Views/Portal/SecureContent.cshtml — Field6/7/8/9), including the one
// spot where display text and stored value differ ("Does not apply" / "Does Not Apply"), so
// the vocabulary stays byte-identical through the Area51 cutover. TODO: these belong in
// API-served reference data alongside reference.supervisors etc., but that isn't spec'd yet —
// until it is, this hardcoded copy is the source of truth for the UI.
const PII_OPTIONS: Partial<Record<keyof SensitiveDraft, Array<{ value: string; label: string }>>> = {
    Gender: [
        { value: "Male", label: "Male" },
        { value: "Female", label: "Female" },
        { value: "Other", label: "Other" },
        { value: "NA", label: "NA" },
    ],
    MaritalStatus: [
        { value: "Single", label: "Single" },
        { value: "Married", label: "Married" },
        { value: "Widowed", label: "Widowed" },
        { value: "Divorced", label: "Divorced" },
    ],
    Ethnicity: [
        { value: "White or Caucasian", label: "White or Caucasian" },
        { value: "American Indian or Alaskan Native", label: "American Indian or Alaskan Native" },
        { value: "Black or African American", label: "Black or African American" },
        { value: "Asian", label: "Asian" },
        { value: "Hispanic or Latino", label: "Hispanic or Latino" },
        { value: "Two or more races", label: "Two or more races" },
        { value: "N/A", label: "N/A" },
        { value: "Native Hawaiian or Pacific Islander", label: "Native Hawaiian or Pacific Islander" },
    ],
    VeteranStatus: [
        { value: "Does Not Apply", label: "Does not apply" },
        { value: "Disabled Individual", label: "Disabled Individual" },
        { value: "Disabled Veteran", label: "Disabled Veteran" },
        { value: "Special Disabled Veteran", label: "Special Disabled Veteran" },
        { value: "Armed Forces Service Medal Veteran", label: "Armed Forces Service Medal Veteran" },
        { value: "Recently Separated Veteran", label: "Recently Separated Veteran" },
        { value: "Veteran of the Vietnam Era", label: "Veteran of the Vietnam Era" },
        { value: "Other Protected Veteran", label: "Other Protected Veteran" },
    ],
}

const PII_MASKS: Record<keyof SensitiveDraft, string> = {
    SSN: "***-**-****",
    HomePhone: "(***) ***-****",
    HomeEmail: "********",
    Birthdate: "**/**/****",
    Gender: "****",
    MaritalStatus: "****",
    Ethnicity: "****",
    VeteranStatus: "****",
}

class UserSecure extends Component<UserSecureProps, UserSecureState> {

    // Re-run the Employees-page health scan after a sensitive-data save clears a MISSING_SENSITIVE_DATA
    // finding. Pay-rate changes refresh via PayRateModal's own context call. No-op outside the panel.
    static contextType = HealthRefreshContext

    state: UserSecureState = {
        loading: true,
        payRate: null,
        record: null,
        draft: { ...EMPTY_DRAFT },
        pristine: { ...EMPTY_DRAFT },
        showPayRateModal: false,
    }

    componentDidMount() {
        this.load()
    }

    toDraft = (record: EmployeeSensitiveData | null): SensitiveDraft => {
        if (!record) return { ...EMPTY_DRAFT }
        return {
            SSN: record.SSN ?? "",
            HomePhone: record.HomePhone ?? "",
            HomeEmail: record.HomeEmail ?? "",
            // API returns a full date-time string; the date input needs yyyy-MM-dd.
            Birthdate: record.Birthdate ? record.Birthdate.slice(0, 10) : "",
            Gender: record.Gender ?? "",
            MaritalStatus: record.MaritalStatus ?? "",
            Ethnicity: record.Ethnicity ?? "",
            VeteranStatus: record.VeteranStatus ?? "",
        }
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            // Fetch only what the caller may read — a call they lack the permission for
            // would be a guaranteed 403 in Release and would fail the whole load.
            const [payRate, record] = await Promise.all([
                this.canReadPay() ? getCurrentPayRate(this.props.userId) : Promise.resolve(null),
                this.canReadPii() ? getEmployeeSensitiveDataByEmployeeId(this.props.userId) : Promise.resolve(null),
            ])
            const draft = this.toDraft(record)
            this.setState({ payRate, record, draft, pristine: draft, loading: false })
        } catch (err) {
            this.setState({ loading: false })
            snackBar({ text: getVault77ErrorMessage(err, "Unable to load secure data."), type: "error", timeout: 5000 })
        }
    }

    handleFieldChange = (field: keyof SensitiveDraft, value: string) => {
        const draft = { ...this.state.draft, [field]: value }
        this.setState({ draft })
        this.reportDirty(draft)
    }

    reportDirty = (draft: SensitiveDraft) => {
        const dirty = JSON.stringify(draft) !== JSON.stringify(this.state.pristine)
        if (dirty !== this.props.secureIsDirty) {
            this.props.manageParentState([{ key: "secureIsDirty", value: dirty }])
        }
    }

    // Called by the parent (via ref) when the top-right Save is clicked and secureIsDirty is true.
    save = async (): Promise<void> => {
        const { record, draft } = this.state
        const payload = {
            SSN: draft.SSN || null,
            HomePhone: draft.HomePhone || null,
            HomeEmail: draft.HomeEmail || null,
            Birthdate: draft.Birthdate || null,
            Gender: draft.Gender || null,
            MaritalStatus: draft.MaritalStatus || null,
            Ethnicity: draft.Ethnicity || null,
            VeteranStatus: draft.VeteranStatus || null,
        }
        try {
            const saved = record
                ? await updateEmployeeSensitiveData({
                    EmployeeSensitiveDataId: record.EmployeeSensitiveDataId,
                    EmployeeId: this.props.userId,
                    ...payload,
                })
                : await createEmployeeSensitiveData({
                    EmployeeId: this.props.userId,
                    ...payload,
                })
            const newDraft = this.toDraft(saved)
            this.setState({ record: saved, draft: newDraft, pristine: newDraft })
            if (this.props.secureIsDirty) {
                this.props.manageParentState([{ key: "secureIsDirty", value: false }])
            }
            ;(this.context as HealthRefreshContextValue).notifyHealthChanged()
            snackBar({ text: "Secure data saved", type: "success", timeout: 3000 })
        } catch (err) {
            snackBar({ text: getVault77ErrorMessage(err, "Unable to save secure data."), type: "error", timeout: 6000 })
            throw err
        }
    }

    togglePayRateModal = (show: boolean) => {
        this.setState({ showPayRateModal: show })
    }

    handlePayRateChanged = async () => {
        try {
            const payRate = await getCurrentPayRate(this.props.userId)
            this.setState({ payRate })
        } catch {
            // The modal's save already succeeded and closed, so its own error handling can't cover
            // this refetch. Clear the stale value so the field shows NA rather than an out-of-date rate.
            this.setState({ payRate: null })
        }
    }

    // Each piece of this card gates on exactly the permission the API checks for the data it
    // shows/saves. The parent renders the card for EITHER read; these decide what's inside.
    // The card keeps its FULL shape for every viewer — non-HRAdmins still see the PII fields,
    // but read-only with static star masks instead of values.
    canReadPay = () => shouldAllowAccess("Vault77.Read.PayRate")
    // PII VALUE visibility mirrors the API's server-side rule: decrypted PII is returned ONLY to
    // the HRAdmin role (IsHRAdmin). A Vault77.Read.EmployeeSensitiveData capability alone still
    // gets masked all-NULL rows back — so gate on HR:ADMIN, or the fields would try to unmask
    // values the API will never send. Managers (pay-read, no HRAdmin) see stars.
    canReadPii = () => shouldAllowAccess("HR:ADMIN")
    canWritePayRate = () => shouldAllowAccess("Vault77.Write.PayRate")

    // The PII fields persist via POST/PUT /api/vault77/employeeSensitiveData, which the API
    // authorizes with Vault77.Write.EmployeeSensitiveData — so editing gates on the same
    // permission. Fields that can't be edited can't go dirty, so save() never fires without it.
    canWritePii = () => shouldAllowAccess("Vault77.Write.EmployeeSensitiveData")

    // Supervisor changes are HRAdmin-only. This mirrors the API-side guard: POST /api/user
    // forces SupervisorId to 0 (= keep existing, per the PortalUpdateUser guard) for any
    // caller without the HRAdmin role, so hiding the picker here is UX — the server is the wall.
    canEditSupervisor = () => shouldAllowAccess("HR:ADMIN")

    renderField = (
        label: string,
        field: keyof SensitiveDraft,
        opts?: { format?: (value: string) => string; maxLength?: number },
    ) => {
        // Masked fields are inert: star placeholder, no change handler, never dirty.
        const masked = !this.canReadPii()
        const readOnly = this.props.readOnly || !this.canWritePii() || masked
        const options = masked ? undefined : PII_OPTIONS[field]
        const value = this.state.draft[field]
        return (
            <div className="uac-secure-col">
                <div className="uac-secure-field">
                    <div id="uac-filled-field"><p>{label}</p></div>
                    {options
                        ? <Select
                            isDisabled={this.state.loading || readOnly}
                            isClearable
                            placeholder="Select..."
                            value={value
                                ? { value, label: options.find((o) => o.value === value)?.label ?? value }
                                : null}
                            options={
                                // Stored values outside the Area51 vocabulary (migrated/seeded data)
                                // still display instead of silently blanking the field.
                                value && !options.some((o) => o.value === value)
                                    ? [{ value, label: value }, ...options]
                                    : options
                            }
                            onChange={(item: any) => this.handleFieldChange(field, item ? item.value : "")}
                            // Same pattern as the CompanyInfo selects: open upward, and portal the
                            // menu to <body> so it can't lose the z-index fight with the rows/cards
                            // below (this page stacks aggressively).
                            menuPlacement="top"
                            maxMenuHeight={175}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                        />
                        : <FormControl
                            disabled={this.state.loading || readOnly}
                            value={masked ? PII_MASKS[field] : value}
                            maxLength={opts?.maxLength}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                !masked && this.handleFieldChange(field, opts?.format ? opts.format(e.target.value) : e.target.value)
                            }
                        />}
                </div>
            </div>
        )
    }

    render() {
        const { loading, payRate, showPayRateModal } = this.state
        const readOnly = this.props.readOnly
        const payRateDisplay = payRate ? formatForDisplay(Number(payRate.PayRate).toFixed(2), "currency") : "*NA*"
        const payRateEffective = payRate ? moment(payRate.EffectiveDate).format("MMM Do YYYY") : null

        const canReadPay = this.canReadPay()
        const canReadPii = this.canReadPii()
        // Without Write.PayRate the modal opens as a read-only history list, so don't call it "Edit".
        const editPayRateTooltip = (
            <Tooltip id="uac-tool-tip">
                {this.canWritePayRate() ? "View / Edit Pay Rate History" : "View Pay Rate History"}
            </Tooltip>
        )

        return (
            <div className="UserSecure">
                <Accordion defaultActiveKey="0" id="user-admin-accordion">
                    <Card id="user-admin-card">
                        <Accordion.Toggle as={Card.Header} eventKey="0">
                            <div id="user-admin-card-title"><p>Secure</p></div>
                        </Accordion.Toggle>
                        <Accordion.Collapse eventKey="0">
                            {loading
                                ? <div className="uac-secure-loading"><Spinner animation="border" variant="secondary" /></div>
                                : <div id="uac-secure-info">
                                {/* Row 0: Supervisor — visible to every card viewer, editable by
                                    HRAdmin only (HR:ADMIN target; API enforces for real). */}
                                {/* Row 0 (top, left-aligned): Pay Rate + Supervisor — the org/comp
                                    pair, matching where the Area51 secure form had them. Pay Rate
                                    needs Vault77.Read.PayRate; Supervisor shows for every card
                                    viewer but is editable by HRAdmin only (API enforces too). */}
                                <div id="uac-secure-r0">
                                    {canReadPay && <div className="uac-secure-col">
                                        <div className="uac-secure-field">
                                            <div className="uac-secure-payrate-header">
                                                <div id="uac-filled-field"><p>Pay Rate</p></div>
                                                <div className="uac-secure-payrate-header-right">
                                                    {/* Icon mirrors what the modal will let you do: pay-rate writers
                                                        (HRAdmin) get the edit pencil, everyone else the history icon. */}
                                                    {/* Always render — any pay-reader may open the modal to VIEW history. The modal
                                                        opens read-only unless the caller holds Vault77.Write.PayRate, so non-HRAdmins
                                                        can view but not edit (see readOnly on PayRateModal below). */}
                                                    {<OverlayTrigger delay={{ show: 250, hide: 400 }} placement="top" overlay={editPayRateTooltip}>
                                                        <span
                                                            className={`material-icons ${this.canWritePayRate() ? "uac-secure-payrate-edit" : "uac-secure-payrate-history"}`}
                                                            onClick={() => this.togglePayRateModal(true)}
                                                        >{this.canWritePayRate() ? "edit" : "history"}</span>
                                                    </OverlayTrigger>}
                                                    {payRateEffective && <OverlayTrigger delay={{ show: 250, hide: 400 }} placement="top" overlay={<Tooltip id="uac-tool-tip">{`Effective: ${payRateEffective}`}</Tooltip>}>
                                                        <span className="material-icons uac-secure-payrate-event">event</span>
                                                    </OverlayTrigger>}
                                                </div>
                                            </div>
                                            <FormControl
                                                readOnly
                                                disabled={loading}
                                                value={loading ? "" : payRateDisplay}
                                            />
                                        </div>
                                    </div>}
                                    {this.props.supervisors && <div className="uac-secure-col">
                                        <div className="uac-secure-field">
                                            <div id="uac-filled-field"><p>Supervisor</p></div>
                                            {this.canEditSupervisor() && !readOnly
                                                ? <Select
                                                    isDisabled={loading}
                                                    value={{ label: this.props.supervisorName || "*NA*" }}
                                                    options={this.props.supervisors.map((s) => ({ value: s.id, label: s.text }))}
                                                    onChange={(item: any) => this.props.onSupervisorChange?.(item.value, item.label)}
                                                    menuPlacement="top"
                                                    maxMenuHeight={175}
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                                : <FormControl disabled value={this.props.supervisorName || "*NA*"} />}
                                        </div>
                                    </div>}
                                </div>
                                {/* PII rows. Fields always render to keep the card's shape; without
                                    Vault77.Read.EmployeeSensitiveData they show read-only star
                                    masks — a manager with pay-read alone sees their report's pay
                                    rate and starred-out PII. */}
                                {/* Row 1: SSN, Birthdate, Home Phone, Home Email */}
                                <div id="uac-secure-r1">
                                    {this.renderField("SSN", "SSN", { format: (v) => formatForDisplay(v, "ssn"), maxLength: 11 })}
                                    <div className="uac-secure-col">
                                        <div className="uac-secure-field">
                                            <div id="uac-filled-field"><p>Birthdate</p></div>
                                            {/* The date picker can't display a star mask, so masked
                                                viewers get a plain read-only input instead. */}
                                            {canReadPii
                                                ? <DatePicker
                                                    value={this.state.draft.Birthdate || null}
                                                    disabled={loading || readOnly || !this.canWritePii()}
                                                    useCloseCallback={true}
                                                    onChange={(date: any) => this.handleFieldChange("Birthdate", moment(date).format("YYYY-MM-DD"))}
                                                />
                                                : <FormControl disabled value={PII_MASKS.Birthdate} />}
                                        </div>
                                    </div>
                                    {this.renderField("Home Phone", "HomePhone", { format: formatPhoneInput, maxLength: 14 })}
                                    {this.renderField("Home Email", "HomeEmail")}
                                </div>
                                {/* Row 2: Gender, Marital Status, Ethnicity, Veteran Status */}
                                <div id="uac-secure-r2">
                                    {this.renderField("Gender", "Gender")}
                                    {this.renderField("Marital Status", "MaritalStatus")}
                                    {this.renderField("Ethnicity", "Ethnicity")}
                                    {this.renderField("Disabled/ Veteran", "VeteranStatus")}
                                </div>
                            </div>}
                        </Accordion.Collapse>
                    </Card>
                </Accordion>

                {showPayRateModal && <PayRateModal
                    employeeId={this.props.userId}
                    readOnly={readOnly || !this.canWritePayRate()}
                    onChanged={this.handlePayRateChanged}
                    onClose={() => this.togglePayRateModal(false)}
                />}
            </div>
        )
    }
}

export default UserSecure
